import React, { useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useBrandPitches } from '../context/BrandPitchesContext';
import { fmtMoney } from '../lib/format';
import { Search, Plus, Users, Handshake, Trash2, ClipboardPaste, CalendarDays, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { STICKY_COLORS, stickyColorFor, clientColorFor } from '../lib/stickyColors';
import EmojiPicker from '../components/EmojiPicker';
import { api } from '../lib/api';
import { toast } from 'sonner';



// One sticky-note per brand shown in the meetings + relationships hot list.
const MeetingSticky = ({ brand, swatch, isManager, onUpdate }) => {
  const [noteEditing, setNoteEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState(brand.meeting_notes || '');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const dateInputRef = React.useRef(null);
  React.useEffect(() => { setNoteDraft(brand.meeting_notes || ''); }, [brand.meeting_notes]);

  const commitNote = () => {
    setNoteEditing(false);
    if ((noteDraft || '') !== (brand.meeting_notes || '')) {
      onUpdate(brand.id, { meeting_notes: noteDraft });
    }
  };

  const openDatePicker = (e) => {
    e.stopPropagation();
    if (!isManager) return;
    if (dateInputRef.current) {
      try { dateInputRef.current.showPicker?.(); } catch {}
      dateInputRef.current.click();
    }
  };

  return (
    <div
      className="relative rounded-xl shadow-sm p-3 flex flex-col overflow-hidden"
      style={{ background: swatch.bg, border: `1px solid ${swatch.border}`, minHeight: 132 }}
      data-testid={`meeting-sticky-${brand.id}`}
    >
      {/* header — emoji + name */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-lg leading-none shrink-0">{brand.logo || '🏷️'}</span>
        <div className="text-sm font-semibold truncate" style={{ color: swatch.ink }} title={brand.name}>{brand.name}</div>
      </div>

      {/* main content — editable short note */}
      <div className="mt-2 flex-1">
        {noteEditing && isManager ? (
          <textarea
            autoFocus
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={commitNote}
            onKeyDown={(e) => { if (e.key === 'Escape') { setNoteEditing(false); setNoteDraft(brand.meeting_notes || ''); } }}
            rows={3}
            className="w-full text-xs bg-transparent outline-none resize-none placeholder:opacity-60"
            style={{ color: swatch.ink }}
            placeholder="jot a note about this brand…"
          />
        ) : (
          <button
            type="button"
            onClick={() => isManager && setNoteEditing(true)}
            disabled={!isManager}
            className={`text-left w-full text-xs whitespace-pre-wrap leading-snug ${isManager ? 'cursor-text' : 'cursor-default'}`}
            style={{ color: swatch.ink }}
          >
            {brand.meeting_notes || (isManager ? <span className="italic opacity-60">+ add a note</span> : '')}
          </button>
        )}
      </div>

      {/* footer — date circle (left) + engaged pill (right) */}
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="relative">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[9px] font-semibold shadow-sm pointer-events-none"
            style={{ background: swatch.tape }}
            title={brand.last_meeting_date ? `last met · ${monthsSince(brand.last_meeting_date)}` : 'click to log a meeting date'}
          >
            {brand.last_meeting_date
              ? new Date(brand.last_meeting_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
              : '+'}
          </div>
          {isManager && (
            <input
              type="date"
              value={brand.last_meeting_date || ''}
              onChange={(e) => onUpdate(brand.id, { last_meeting_date: e.target.value || null })}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              data-testid={`meeting-date-circle-${brand.id}`}
              title="pick a meeting date"
            />
          )}
        </div>
        {brand._engaged && (
          <span
            className="text-[9px] font-semibold uppercase tracking-widest rounded-full px-1.5 py-0.5"
            style={{ background: `${swatch.tape}40`, color: swatch.ink }}
          >engaged</span>
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Meetings + Relationships — track when we last met each brand.
// Brands met within the last 3 months are considered "engaged" and get an
// automatic 📆 sticker across the pitching grid + talent view.
// -----------------------------------------------------------------------------
const monthsSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (months <= 0) return 'this month';
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? '1 year ago' : `${years} years ago`;
};

const BrandRatingStars = ({ value = 0, onChange, disabled }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="inline-flex items-center gap-0.5" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange?.(n === value ? 0 : n); }}
            onMouseEnter={() => !disabled && setHover(n)}
            className="p-0.5 leading-none disabled:cursor-default"
            title={`${n} star${n === 1 ? '' : 's'}`}
            data-testid={`brand-rating-${n}`}
          >
            <svg viewBox="0 0 24 24" className={`w-3.5 h-3.5 ${filled ? 'fill-pink-400 text-pink-400' : 'fill-transparent text-slate-300'}`} stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

const MeetingsRelationships = ({ brands, onUpdate, isManager }) => {
  const [open, setOpen] = useState(true);
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [range, setRange] = useState('all'); // 'all' | 'this_month' | 'last_month' | 'this_quarter' | 'last_quarter' | 'reengage'

  const enriched = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    // window boundaries (inclusive start, exclusive end)
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const currentQ = Math.floor(now.getMonth() / 3);
    const thisQuarterStart = new Date(now.getFullYear(), currentQ * 3, 1);
    const nextQuarterStart = new Date(now.getFullYear(), currentQ * 3 + 3, 1);
    const lastQuarterStart = new Date(now.getFullYear(), currentQ * 3 - 3, 1);

    return (brands || [])
      .filter((b) => (b.rating || 0) >= 4)
      .map((b) => {
        const d = b.last_meeting_date ? new Date(b.last_meeting_date) : null;
        const valid = d && !isNaN(d.getTime());
        return {
          ...b,
          _d: valid ? d : null,
          _hasMeeting: valid,
          _engaged: valid && d >= cutoff,
          _windows: {
            this_month:   valid && d >= thisMonthStart && d < nextMonthStart,
            last_month:   valid && d >= lastMonthStart && d < thisMonthStart,
            this_quarter: valid && d >= thisQuarterStart && d < nextQuarterStart,
            last_quarter: valid && d >= lastQuarterStart && d < thisQuarterStart,
            reengage:     !valid || d < cutoff,
          },
        };
      });
  }, [brands]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = enriched.filter((b) => {
      if (range !== 'all' && !b._windows[range]) return false;
      if (!s) return true;
      return (b.name || '').toLowerCase().includes(s);
    });
    return rows.sort((a, b) => {
      if (range === 'reengage') {
        // brands with no meeting first, then oldest first (most in need)
        if (!a._d && !b._d) return (a.name || '').localeCompare(b.name || '');
        if (!a._d) return -1;
        if (!b._d) return 1;
        return a._d - b._d;
      }
      if (!a.last_meeting_date && !b.last_meeting_date) return (a.name || '').localeCompare(b.name || '');
      if (!a.last_meeting_date) return 1;
      if (!b.last_meeting_date) return -1;
      return String(b.last_meeting_date).localeCompare(String(a.last_meeting_date));
    });
  }, [enriched, q, range]);

  const shown = showAll ? filtered : filtered.slice(0, 12);
  const engagedCount = enriched.filter((b) => b._engaged).length;
  const reengageCount = enriched.filter((b) => b._windows.reengage).length;

  return (
    <section className="mb-8 bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
        data-testid="meetings-toggle"
      >
        <CalendarDays className="w-5 h-5 text-pink-500" />
        <div className="text-left">
          <div className="text-base font-semibold text-slate-900">meetings + relationships</div>
        </div>
        <div className="ml-auto text-slate-400">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</div>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <div className="relative max-w-sm flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="find a brand…" className="pl-9 h-9" />
            </div>
            <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5" data-testid="meetings-range-tabs">
              {[
                { id: 'all',          label: 'all' },
                { id: 'this_month',   label: 'this month' },
                { id: 'last_month',   label: 'last month' },
                { id: 'this_quarter', label: 'this quarter' },
                { id: 'last_quarter', label: 'last quarter' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setRange(opt.id)}
                  className={`px-3 h-8 text-[11px] font-semibold rounded-full transition ${range === opt.id ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  data-testid={`meetings-range-${opt.id}`}
                >{opt.label}</button>
              ))}
            </div>
            <button
              onClick={() => setRange(range === 'reengage' ? 'all' : 'reengage')}
              className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold border transition ${range === 'reengage' ? 'bg-rose-500 text-white border-rose-500' : 'bg-white text-rose-600 border-rose-200 hover:bg-rose-50'}`}
              title="brands rated 4-5⭐ that you haven't met with in the last 3 months"
              data-testid="meetings-range-reengage"
            >to engage · {reengageCount}</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {shown.map((b, i) => {
              const paletteId = STICKY_COLORS[i % STICKY_COLORS.length].id;
              const swatch = stickyColorFor(paletteId);
              return (
                <MeetingSticky
                  key={b.id}
                  brand={b}
                  swatch={swatch}
                  isManager={isManager}
                  onUpdate={onUpdate}
                />
              );
            })}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-sm text-slate-400 py-6">no 4-5⭐ brands yet — rate a brand 4 or 5 stars on its card to add it here</div>
            )}
          </div>
          {filtered.length > 12 && (
            <div className="mt-3 text-center">
              <button
                onClick={() => setShowAll((s) => !s)}
                className="text-xs font-semibold text-pink-600 hover:text-pink-700"
                data-testid="meetings-show-all"
              >{showAll ? 'show fewer' : `show all ${filtered.length}`}</button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const BulkBrandsDialog = ({ open, onOpenChange, onDone }) => {
  const [raw, setRaw] = useState('');
  const [saving, setSaving] = useState(false);
  const names = raw.split('\n').map((s) => s.trim()).filter(Boolean);

  const submit = async () => {
    if (!names.length) return;
    setSaving(true);
    try {
      const { data } = await api.post('/brands/bulk', { names });
      toast.success(`added ${data.created_count} brand${data.created_count === 1 ? '' : 's'}`, {
        description: data.skipped_count ? `${data.skipped_count} skipped (already existed)` : undefined,
      });
      setRaw('');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error('failed to add brands');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>bulk add brands</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">paste one brand per line — duplicates are skipped automatically.</p>
        <Textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={12}
          placeholder={`Charlotte Tilbury\nDrunk Elephant + Shiseido + Nars\nSephora\n…`}
          className="mt-3 font-mono text-sm"
          data-testid="bulk-brands-textarea"
        />
        <div className="text-[11px] text-slate-500 mt-1">{names.length} brand{names.length === 1 ? '' : 's'} ready to add</div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button disabled={!names.length || saving} onClick={submit} className="bg-pink-500 hover:bg-pink-600 text-white font-semibold" data-testid="bulk-brands-submit">
            {saving ? 'adding…' : `add ${names.length} brand${names.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddBrandDialog = ({ open, onOpenChange }) => {
  const { addBrand } = useDeals();
  const [name, setName] = useState('');
  const [logo, setLogo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  const submit = async () => {
    if (!name.trim()) { setError('Brand name required'); return; }
    setSaving(true); setError(null);
    try {
      await addBrand(name.trim(), logo.trim());
      setName(''); setLogo(''); onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add brand');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRef}
        className="max-w-md"
        onPointerDownOutside={(e) => {
          // keep the dialog open when the on-screen emoji keyboard / emoji picker fires a
          // pointerdown that Radix mistakes for an outside tap
          if (e.target?.closest?.('[data-emoji-ui="true"]')) { e.preventDefault(); return; }
          if (!contentRef.current) return;
          const r = contentRef.current.getBoundingClientRect();
          const oe = e.detail?.originalEvent || {};
          const { clientX, clientY } = oe;
          if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) e.preventDefault();
        }}
      >
        <DialogHeader><DialogTitle>add brand</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="flex items-end gap-3">
            <div>
              <Label>Emoji</Label>
              <div className="mt-1">
                <EmojiPicker value={logo} onChange={setLogo} size="lg" testId="brand-emoji-picker" />
              </div>
            </div>
            <div className="flex-1">
              <Label>Brand name</Label>
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aritzia" onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
            </div>
          </div>
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button disabled={saving} onClick={submit} className="bg-pink-500 hover:bg-pink-600 text-white font-semibold">{saving ? 'adding…' : 'add brand'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Brands = () => {
  const { user } = useAuth();
  const { brands, deals, talent, talentMap, removeBrand, updateBrand, refresh } = useDeals();
  const { items: brandPitchItems } = useBrandPitches();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sortMode, setSortMode] = useState('value'); // 'value' | 'alpha'
  const isManager = ['manager', 'talent_manager'].includes(user?.role);

  const brandRows = useMemo(() => {
    return brands.map((b) => {
      const brandDeals = deals.filter((d) => d.brand_id === b.id);
      const contacts = b.contacts || [];
      const revenue = brandDeals.reduce((s, d) => s + (d.value || 0), 0);
      const paid = brandDeals.filter((d) => d.stage === 'paid_out').reduce((s, d) => s + (d.value || 0), 0);
      const pitchRating = Math.max(0, ...(brandPitchItems || []).filter((bp) => bp.brand_id === b.id).map((bp) => bp.stars || 0), 0);
      return { ...b, brandDeals, contacts, revenue, paid, pitchRating };
    });
  }, [brands, deals, brandPitchItems]);

  // Filter: match brand name OR any contact name/email OR any linked deal title/talent name
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? brandRows.filter((b) => {
      if ((b.name || '').toLowerCase().includes(s)) return true;
      if ((b.contacts || []).some((c) => `${c.name || ''} ${c.email || ''} ${c.role || ''}`.toLowerCase().includes(s))) return true;
      if ((b.brandDeals || []).some((d) => (d.title || '').toLowerCase().includes(s))) return true;
      if ((b.brandDeals || []).some((d) => (talentMap[d.talent_id]?.name || '').toLowerCase().includes(s))) return true;
      return false;
    }) : brandRows;
    if (sortMode === 'alpha') {
      return [...list].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
    }
    // 'value' — by lifetime £ revenue desc, then alpha
    return [...list].sort((a, b) => (b.revenue || 0) - (a.revenue || 0) || (a.name || '').localeCompare(b.name || ''));
  }, [brandRows, q, talentMap, sortMode]);

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">brands</h1>
          <p className="text-sm text-slate-500 mt-1">everyone you work with. keep contacts, notes and deals per brand.</p>
        </div>
        {isManager && (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setBulkOpen(true)} className="border border-slate-200" data-testid="bulk-add-brands-btn">
              <ClipboardPaste className="w-4 h-4 mr-1.5" /> bulk paste
            </Button>
            <Button onClick={() => setOpen(true)} className="bg-pink-500 hover:bg-pink-600 text-white font-semibold">
              <Plus className="w-4 h-4 mr-1.5" /> add brand
            </Button>
          </div>
        )}
      </header>

      <MeetingsRelationships brands={brands} onUpdate={updateBrand} isManager={isManager} />

      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">brand list · {filtered.length}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="filter by brand, contact, deal or talent…" className="pl-9 h-9 bg-white" />
          </div>
          <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5" data-testid="brands-sort-tabs">
            {[
              { id: 'value', label: 'by £ revenue' },
              { id: 'alpha', label: 'A–Z' },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setSortMode(opt.id)}
                className={`px-3 h-8 text-[11px] font-semibold rounded-full transition ${sortMode === opt.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                data-testid={`brands-sort-${opt.id}`}
              >{opt.label}</button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <p className="text-slate-500 text-sm">no brands match your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b) => (
            <Link key={b.id} to={`/brands/${b.id}`} className="group bg-white border border-slate-200 hover:border-mint-300 hover:shadow-md rounded-2xl p-5 transition">
              <div className="flex items-start gap-3">
                {isManager ? (
                  <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <EmojiPicker
                      value={b.logo || ''}
                      onChange={(v) => updateBrand(b.id, { logo: v })}
                      size="lg"
                      testId={`brand-emoji-${b.id}`}
                    />
                  </div>
                ) : (
                  <div className="w-11 h-11 rounded-xl bg-mint-50 text-lg flex items-center justify-center">{b.logo || b.name.charAt(0)}</div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{b.name}</h3>
                  <div className="mt-0.5" onClick={(e) => e.preventDefault()}>
                    <BrandRatingStars
                      value={b.rating || 0}
                      disabled={!isManager}
                      onChange={(v) => updateBrand(b.id, { rating: v })}
                    />
                  </div>
                  {b.notes && <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{b.notes}</p>}
                </div>
                {isManager && (
                  <button onClick={(e) => { e.preventDefault(); if (window.confirm(`Delete ${b.name}? Deals will be unlinked from this brand.`)) removeBrand(b.id); }} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-4 gap-2 text-center">
                <div><div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center justify-center gap-1"><Users className="w-3 h-3" />contacts</div><div className="text-sm font-semibold text-slate-800">{b.contacts.length}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center justify-center gap-1"><Handshake className="w-3 h-3" />deals</div><div className="text-sm font-semibold text-slate-800">{b.brandDeals.length}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-slate-400">revenue</div><div className="text-sm font-semibold text-mint-700">{fmtMoney(b.revenue)}</div></div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">pitch rating</div>
                  <div className="mt-0.5 flex items-center justify-center" data-testid={`brand-pitch-rating-${b.id}`}>
                    {b.pitchRating > 0 ? <BrandRatingStars value={b.pitchRating} disabled /> : <span className="text-sm text-slate-300">—</span>}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <AddBrandDialog open={open} onOpenChange={setOpen} />
      <BulkBrandsDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={refresh} />
    </div>
  );
};

export default Brands;
